import React, { useEffect } from 'react';
import { CCol, CRow } from '@coreui/react';
import moment from 'moment';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import LoadingAvatar from '../../components/LoadingAvatar';
import { ModalHeader } from '../../components/Modals';
import { WizardModalLayout } from '../../components/ModalScrollProgress';
import { StyledModalCardV2 } from '../../components/modalV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../components/WizardV2/wizardStylesV2';
import {
  EntityCoverBadge,
  EntityImagesGrid,
  EntityImagesSection,
  EntityImageThumb,
  EntityImageTile,
  mapEntityImages
} from '../../components/EntityImagesGallery';
import { Palette } from '../../components/utils';
import { ImagesOptions } from '../../helper';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { getEvent } from '../../redux/actions/events';
import { setToast } from '../../redux/actions/window';
import { GET_EVENT } from '../../redux/constants';
import { requestPending, requestSuccess } from '../../utils/status';
import { LOADING_BLUE } from '../../utils/colorCode';
import { SettingsModalBoldText } from '../settings/SettingsModals';
import {
  SettingsItem,
  SettingsItemContent,
  SettingsItemSubTitle,
  SettingsItemTitle
} from '../settings/SettingsItem';
import { isQuillContentEmpty, MediaKeys } from '../offers/utils';
import { DATE_FORMATS } from '../users/constant';
import {
  EVENT_MODAL_PROPS,
  getEventImageUrl,
  StickyEventModalHeader
} from './AddEditEvent';
import { formatEventTime, formatEventTimeRange, hasEventStarted, isAllDayEvent } from './Events';
import { EVENT_IMAGE_TYPE, EVENT_STATUS, EVENT_STATUS_LABEL, isEventVisible } from './constants';

const EVENT_IMAGE_DIMENSIONS = ImagesOptions();
const BANNER_DIMENSIONS = EVENT_IMAGE_DIMENSIONS[MediaKeys.EVENT_BANNER].DIMENSION;
const THUMBNAIL_DIMENSIONS = EVENT_IMAGE_DIMENSIONS[MediaKeys.EVENT_THUMBNAIL].DIMENSION;

const DATE_DISPLAY_FORMAT = DATE_FORMATS.MMM_DD_YYYY;

const formatEventDate = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DATE_DISPLAY_FORMAT) : '-';
};

const displayValue = (value) => {
  if (value == null) {
    return '-';
  }
  const trimmed = String(value).trim();
  return trimmed || '-';
};

const DetailField = ({ label, children, md }) => (
  <CCol md={md}>
    <SettingsItem>
      <SettingsItemContent>
        <SettingsItemTitle>{label}</SettingsItemTitle>
        <SettingsItemSubTitle>{children}</SettingsItemSubTitle>
      </SettingsItemContent>
    </SettingsItem>
  </CCol>
);

const EventDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const modal = useSelector((state) => state.headerModal.modal);
  const eventData = useSelector((state) => state.headerModal.data);
  const event = useSelector((state) => state.events.event);
  const getEventStatus = useSelector((state) => state.events.getEventStatus);
  const globalSelectedBuilding = useSelector(
    (state) => state.building.globalSelectedBuilding
  );

  const show = modal === 'eventDetails';
  const eventId = eventData?.id;

  const isLoading =
    show &&
    (getEventStatus === requestPending(GET_EVENT) ||
      !event?.id ||
      (eventId != null && Number(event.id) !== Number(eventId)));

  const isEventLoaded =
    show &&
    getEventStatus === requestSuccess(GET_EVENT) &&
    event &&
    Number(event.id) === Number(eventId);

  useEffect(() => {
    if (!show || !eventId) {
      return;
    }

    dispatch(
      getEvent({
        id: eventId,
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to load event!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [show, eventId, dispatch, t]);

  const onClose = () => {
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const handleEdit = () => {
    if (!eventId || hasEventStarted(event?.event_date, event?.start_time, globalSelectedBuilding?.state)) {
      return;
    }
    dispatch(
      changeOpenedModal({
        modal: 'addEditEvent',
        data: { id: eventId }
      })
    );
  };

  const bannerUrl = isEventLoaded ? getEventImageUrl(event, EVENT_IMAGE_TYPE.BANNER) : '';
  const thumbnailUrl = isEventLoaded
    ? getEventImageUrl(event, EVENT_IMAGE_TYPE.THUMBNAIL)
    : '';
  const additionalImages = isEventLoaded
    ? mapEntityImages(event.additional_images || event.images || [])
    : [];
  const descriptionHtml = isEventLoaded ? event.description || '' : '';
  const hasDescription = !isQuillContentEmpty(descriptionHtml);
  const statusLabel = isEventLoaded
    ? t(EVENT_STATUS_LABEL[isEventVisible(event) ? EVENT_STATUS.VISIBLE : EVENT_STATUS.HIDDEN])
    : '-';
  const canEdit = !(
    isEventLoaded && hasEventStarted(event?.event_date, event?.start_time, globalSelectedBuilding?.state)
  );

  if (!show) {
    return null;
  }

  return (
    <WizardModalLayout
      Modal={StyledModalCardV2}
      modalProps={EVENT_MODAL_PROPS}
      show={show}
      onClose={onClose}
      width="548px"
      bodyGap="24px"
      closeOnBackdrop={false}
      deps={[show, eventId, isLoading]}
      footerActions={
        <>
          <WizardFooterButtonSecondary type="button" onClick={onClose}>
            {t('Close')}
          </WizardFooterButtonSecondary>
          {canEdit && (
            <WizardFooterButtonPrimary
              type="button"
              onClick={handleEdit}
              disabled={isLoading || !eventId}>
              {t('Edit')}
            </WizardFooterButtonPrimary>
          )}
        </>
      }>
      <StickyEventModalHeader>
        <ModalHeader title={t('Event Details')} v2 onCloseCallback={onClose} />
      </StickyEventModalHeader>

      {isLoading ? (
        <LoadingAvatar color={LOADING_BLUE} />
      ) : (
        <>
          <CRow>
            <DetailField label={t('Title')}>{displayValue(event?.title)}</DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Subheading')}>
              {displayValue(event?.sub_heading)}
            </DetailField>
          </CRow>

          {bannerUrl ? (
            <CRow>
              <CCol>
                <SettingsModalBoldText>{t('Event Banner')}</SettingsModalBoldText>
                <EventImagePreview
                  src={bannerUrl}
                  alt={t('Event Banner')}
                  $width={BANNER_DIMENSIONS.WIDTH}
                  $height={BANNER_DIMENSIONS.HEIGHT}
                />
              </CCol>
            </CRow>
          ) : null}

          {thumbnailUrl ? (
            <CRow>
              <CCol>
                <SettingsModalBoldText>{t('Event Thumbnail')}</SettingsModalBoldText>
                <EventImagePreview
                  src={thumbnailUrl}
                  alt={t('Event Thumbnail')}
                  $width={THUMBNAIL_DIMENSIONS.WIDTH}
                  $height={THUMBNAIL_DIMENSIONS.HEIGHT}
                />
              </CCol>
            </CRow>
          ) : null}

          <CRow>
            <DetailField label={t('Description')}>
              {hasDescription ? ReactHtmlParser(descriptionHtml) : '-'}
            </DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Event Date')}>
              {formatEventDate(event?.event_date)}
            </DetailField>
          </CRow>

          <CRow>
            {isAllDayEvent(event?.start_time, event?.end_time) ? (
              <DetailField label={t('Time')}>
                {t('All Day')}
              </DetailField>
            ) : (
              <>
                <DetailField label={t('Start Time')} md="6">
                  {formatEventTime(event?.start_time) || '-'}
                </DetailField>
                <DetailField label={t('End Time')} md="6">
                  {formatEventTime(event?.end_time) || '-'}
                </DetailField>
              </>
            )}
          </CRow>

          <CRow>
            <DetailField label={t('Location')}>{displayValue(event?.location)}</DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Tag')}>{displayValue(event?.tag)}</DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('CTA Text')} md="6">
              {displayValue(event?.cta_text)}
            </DetailField>
            <DetailField label={t('CTA Link')} md="6">
              {displayValue(event?.cta_link)}
            </DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Status')}>{statusLabel}</DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Pinned')}>
              {isEventLoaded && event?.pinned ? t('Yes') : t('No')}
            </DetailField>
          </CRow>

          <CRow>
            <DetailField label={t('Last Notified')}>
              {event?.notification?.notified_date
                ? moment(event.notification.notified_date).format(`${DATE_DISPLAY_FORMAT} ${DATE_FORMATS.hh_mm_A}`)
                : '-'}
            </DetailField>
          </CRow>

          {additionalImages.length > 0 ? (
            <CRow>
              <CCol>
                <SettingsModalBoldText>{t('Additional Images')}</SettingsModalBoldText>
                <EntityImagesSection style={{ marginTop: 12 }}>
                  <EntityImagesGrid>
                    {additionalImages.map((img, index) => (
                      <EntityImageTile key={img.id}>
                        {index === 0 ? (
                          <EntityCoverBadge>{t('Cover')}</EntityCoverBadge>
                        ) : null}
                        <EntityImageThumb src={img.preview} alt="" draggable={false} />
                      </EntityImageTile>
                    ))}
                  </EntityImagesGrid>
                </EntityImagesSection>
              </CCol>
            </CRow>
          ) : null}
        </>
      )}
    </WizardModalLayout>
  );
};

const EventImagePreview = styled.img`
  display: block;
  width: ${({ $width }) => `${$width}px`};
  height: ${({ $height }) => `${$height}px`};
  max-width: 100%;
  margin-top: 12px;
  object-fit: cover;
  border-radius: 10px;
  background: ${Palette.GHOST_WHITE};
`;

export default EventDetails;
